export interface RegistrationStream {
  id: string;
  name: string;
  // FK link — the source of truth for registration status. A stream linked
  // through the "+ State Registration" dialog carries only this; its legacy
  // triple stays null.
  registrationId?: string | null;
  stateRegisteredId?: string | null;
  stateRegistrationStatus?: string | null;
  stateRegisteredAt?: string | null;
}

const STATUS_RANK: Record<string, number> = {
  NOT_REGISTERED: 0,
  IN_PROGRESS: 1,
  REGISTERED: 2,
};

function rank(status: string | null | undefined): number {
  if (status == null) return -1;
  return STATUS_RANK[status] ?? -1;
}

function timestamp(at: string | null | undefined): number {
  if (at == null) return 0;
  const parsed = new Date(at).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
}

/** Streams linked to a registration through either the FK or the legacy id. */
export function getRegistrationCandidates(streams: RegistrationStream[]): RegistrationStream[] {
  return streams.filter(
    (stream) => stream.registrationId != null || stream.stateRegisteredId != null
  );
}

/**
 * Distinct-registration key. Prefers the FK; for a stream the backfill hasn't
 * linked yet it falls back to the full legacy triple, so two streams sharing a
 * legacy id but differing in status/timestamp still prompt a choice. Keyed at
 * least as finely as the server's conflict check (FK, else legacy id), so the
 * picker always appears when the server would otherwise hard-conflict — a
 * source is then sent and the server honors it instead of blocking.
 */
function registrationKey(stream: RegistrationStream): string {
  return (
    stream.registrationId ??
    `legacy:${stream.stateRegisteredId}|${stream.stateRegistrationStatus ?? ""}|${
      stream.stateRegisteredAt ?? ""
    }`
  );
}

/**
 * True when the registered streams disagree and the user must pick which
 * registration survives. False when none/one is registered or all registered
 * streams point at the same registration.
 */
export function needsRegistrationChoice(streams: RegistrationStream[]): boolean {
  const candidates = getRegistrationCandidates(streams);
  if (candidates.length < 2) return false;
  const distinctKeys = new Set(candidates.map(registrationKey));
  return distinctKeys.size > 1;
}

/** The id of the registered stream whose registration should be pre-selected. */
export function defaultRegistrationSourceId(streams: RegistrationStream[]): string | undefined {
  const candidates = getRegistrationCandidates(streams);
  if (candidates.length === 0) return undefined;
  const winner = candidates.reduce((best, candidate) => {
    const bestRank = rank(best.stateRegistrationStatus);
    const candidateRank = rank(candidate.stateRegistrationStatus);
    if (candidateRank !== bestRank) {
      return candidateRank > bestRank ? candidate : best;
    }
    return timestamp(candidate.stateRegisteredAt) > timestamp(best.stateRegisteredAt)
      ? candidate
      : best;
  });
  return winner.id;
}
